import { Link } from "react-router-dom";
import { Car, PaintBucket, Sparkles, Radar, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Img } from "./Img";

const SERVICES = [
  {
    id: "service-card-collision",
    link: "/services/collision-repair",
    icon: Car,
    badge: "Frame & Unibody",
    title: "Collision Repair & Structural Alignment",
    desc: "Computerized chassis measurements, unibody bench straightening, and heavy structural collision restoration exceeding OEM factory specifications.",
    img: "collision-repair-carrollton-tx",
    alt: "Certified collision repair on a luxury vehicle at Westway Collision Center in Carrollton, TX",
    span: "lg:col-span-7",
  },
  {
    id: "service-card-paint",
    link: "/services/auto-body-paint",
    icon: PaintBucket,
    badge: "Spectrophotometer Matched",
    title: "Car Body & Paint Repair",
    desc: "State-of-the-art down-draft paint booths paired with Sherwin-Williams computerized color spectrophotometers for undetectable factory finish matches.",
    img: "rolls-royce-luxury-detailing-carrollton",
    alt: "Rolls-Royce paint finish inspection and body work at Westway Collision Center in Carrollton, TX",
    span: "lg:col-span-5",
  },
  {
    id: "service-card-pdr",
    link: "/services/paintless-dent-repair",
    icon: Sparkles,
    badge: "Clear-Coat Preserved",
    title: "Paintless Dent Repair (PDR)",
    desc: "Precision specialized PDR tools to remove hail damage, parking dings, and body creases while preserving your original factory clear-coat.",
    img: "lamborghini-body-panel-repair-dfw",
    alt: "Paintless dent repair on a luxury panel at Westway Collision Center in Carrollton, TX",
    span: "lg:col-span-5",
  },
  {
    id: "service-card-adas",
    link: "/services/adas-calibration",
    icon: Radar,
    badge: "OEM Spec Calibrated",
    title: "ADAS Calibration",
    desc: "Precision digital recalibration of modern automotive safety sensors, blind-spot radars, lane assist cameras, and LiDAR post-collision.",
    img: "corvette-repair-after-carrollton",
    alt: "Chevrolet Corvette finished to factory standard at Westway Collision Center in Carrollton, TX",
    span: "lg:col-span-7",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>The Craft</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl text-balance">
          Engineered Restoration, <span className="accent-text">Not Just Repair</span>
        </h2>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">
          Four disciplines. One standard: factory pre-loss condition. Every procedure follows OEM
          repair methodology — documented, measured, and guaranteed in writing.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-7">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className={`col-span-1 ${s.span}`}>
            <Link
              to={s.link}
              data-testid={s.id}
              className="group relative block h-full overflow-hidden rounded-2xl border border-white/[0.08] bg-surface transition-all duration-500 hover:border-accent/30 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-24px_rgba(var(--brand-accent),0.22)]"
            >
              <div className="relative h-52 sm:h-60 overflow-hidden">
                <Img name={s.img} alt={s.alt} className="h-full w-full object-cover" sizes="(min-width: 1024px) 50vw, 100vw" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" aria-hidden="true" />
                <span className="absolute top-4 left-4 inline-flex items-center rounded-full border border-accent/30 bg-ink/70 backdrop-blur-md px-3.5 py-1.5 font-mono text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-accent">
                  {s.badge}
                </span>
              </div>
              <div className="relative p-6 sm:p-8">
                <s.icon size={26} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-slate-100 group-hover:text-accent transition-colors">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-accent/80 group-hover:text-accent transition-colors">
                  Learn more <ArrowUpRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
